import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Popconfirm,
  Row,
  Col,
  Typography,
  Divider,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import PageTitle from "../page-header/PageHeader";
import {
  fetchAllLayerTemplates,
  createLayerTemplate,
  updateLayerTemplate,
  deleteLayerTemplate,
} from "../../redux/actions/layerTemplateActions";
import rawData from "../../data/rawMaterialData";

const { Title, Text } = Typography;

const LAYER_TYPES = ["top", "flute", "face", "bottom"];

/** Inline row editor for one layer-type config */
const LayerTypeRow = ({ type, config, onChange }) => {
  const gsmOptions = config.materialType ? rawData.materialGsm[config.materialType] || [] : [];
  const bfOptions = config.materialType ? rawData.materialBf[config.materialType] || [] : [];
  const isFlute = type === "flute";

  const set = (field, val) => onChange({ ...config, [field]: val });

  return (
    <Row gutter={12} align="middle" style={{ marginBottom: 8 }}>
      <Col flex="80px">
        <Tag color={isFlute ? "orange" : "blue"} style={{ width: "100%", textAlign: "center" }}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </Tag>
      </Col>
      <Col flex="auto">
        <Select value={config.materialType || undefined} placeholder="Material" style={{ width: "100%" }} size="small"
          onChange={(v) => onChange({ ...config, materialType: v, gsm: "", bf: "" })}>
          {rawData.materialType.map((t) => (<Select.Option key={t} value={t}>{t}</Select.Option>))}
        </Select>
      </Col>
      <Col flex="auto">
        <Select value={config.gsm || undefined} placeholder="GSM" style={{ width: "100%" }} size="small"
          onChange={(v) => set("gsm", v)}>
          {gsmOptions.map((g) => (<Select.Option key={g} value={g}>{g}</Select.Option>))}
        </Select>
      </Col>
      <Col flex="auto">
        <Select value={config.bf || undefined} placeholder="BF" style={{ width: "100%" }} size="small"
          onChange={(v) => set("bf", v)}>
          {bfOptions.map((b) => (<Select.Option key={b} value={b}>{b}</Select.Option>))}
        </Select>
      </Col>
      {isFlute && (
        <Col flex="auto">
          <Select value={config.ff || undefined} placeholder="FF" style={{ width: "100%" }} size="small"
            onChange={(v) => set("ff", v)}>
            {rawData.ff.map((f) => (<Select.Option key={f} value={f}>{f}</Select.Option>))}
          </Select>
        </Col>
      )}
    </Row>
  );
};

const LayerTemplateManager = () => {
  const dispatch = useDispatch();
  const { list: templates, loading } = useSelector((s) => s.layerTemplates);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [layerConfig, setLayerConfig] = useState({ top: {}, flute: {}, face: {}, bottom: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => { dispatch(fetchAllLayerTemplates()); }, [dispatch]);

  // ── Helpers ──────────────────────────────────────────────────────────
  const emptyConfig = () => ({ top: {}, flute: {}, face: {}, bottom: {} });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setLayerConfig(emptyConfig());
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description || "",
      display_order: record.display_order || 0,
    });
    setLayerConfig(record.layer_config || emptyConfig());
    setModalOpen(true);
  };

  const openClone = (record) => {
    setEditing(null);
    form.setFieldsValue({
      name: `${record.name} (copy)`,
      description: record.description || "",
      display_order: (record.display_order || 0) + 1,
    });
    setLayerConfig(record.layer_config || emptyConfig());
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!layerConfig.top?.materialType) {
        toast.error("Please configure at least the Top layer");
        return;
      }

      setSaving(true);
      const payload = {
        name: values.name.trim(),
        layer_config: layerConfig,
        description: values.description?.trim() || null,
        display_order: values.display_order ?? 0,
      };

      if (editing) {
        await dispatch(updateLayerTemplate(editing.id, payload));
      } else {
        await dispatch(createLayerTemplate(payload));
      }
      setModalOpen(false);
      dispatch(fetchAllLayerTemplates());
    } catch (_) { /* error toast handled by actions */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await dispatch(deleteLayerTemplate(id));
    dispatch(fetchAllLayerTemplates());
  };

  // ── Summary tag for a config ────────────────────────────────────────
  const cfgTag = (cfg, type) => {
    if (!cfg?.materialType) return null;
    return (
      <Tag key={type} style={{ marginBottom: 2 }}>
        {type}: {cfg.materialType} {cfg.gsm || ""} {cfg.bf ? `BF${cfg.bf}` : ""}
        {type === "flute" && cfg.ff ? ` FF${cfg.ff}` : ""}
      </Tag>
    );
  };

  // ── Table columns ───────────────────────────────────────────────────
  const columns = [
    { title: "Name", dataIndex: "name", key: "name", sorter: (a, b) => a.name.localeCompare(b.name) },
    {
      title: "Layer Config",
      key: "layer_config",
      render: (_, r) => {
        const cfg = r.layer_config || {};
        return LAYER_TYPES.map((t) => cfgTag(cfg[t], t));
      },
    },
    { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
    {
      title: "Active", dataIndex: "is_active", key: "is_active", width: 80,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    {
      title: "Order", dataIndex: "display_order", key: "display_order", width: 70,
      sorter: (a, b) => a.display_order - b.display_order,
    },
    {
      title: "Actions", key: "actions", width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Button type="link" icon={<CopyOutlined />} size="small" onClick={() => openClone(record)} />
          <Popconfirm title="Delete this template?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <>
      <PageTitle title="Back" />
      <Card
        title={<Title level={4} style={{ margin: 0 }}>Layer Templates</Title>}
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Template</Button>}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Templates store material combinations for Top / Flute / Face / Bottom layers.
          They can be applied to any ply value (3 / 5 / 7 / 9).
        </Text>
        <Table dataSource={templates} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} size="small" />
      </Card>

      {/* ── Create / Edit Modal ─────────────────────────────────────── */}
      <Modal
        title={editing ? "Edit Layer Template" : "New Layer Template"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        width={680}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Template Name" rules={[{ required: true, message: "Name is required" }]}>
                <Input placeholder='e.g. "Kraft / Semi Standard"' maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="display_order" label="Display Order">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} maxLength={250} />
          </Form.Item>
        </Form>

        <Divider style={{ margin: "8px 0 12px" }}>Layer Configurations</Divider>
        <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
          Configure the material for each layer type. When applied, all flute layers get the Flute config,
          all face layers get the Face config, etc.
        </Text>

        {LAYER_TYPES.map((type) => (
          <LayerTypeRow
            key={type}
            type={type}
            config={layerConfig[type] || {}}
            onChange={(newCfg) => setLayerConfig((prev) => ({ ...prev, [type]: newCfg }))}
          />
        ))}
      </Modal>
    </>
  );
};

export default LayerTemplateManager;
